import React from "react";
import { Link } from "react-router-dom";
import "./NamiZenPrivacy.css";

const NamiZenPrivacy = () => (
  <div className="namizen-privacy">
    <div className="namizen-privacy__container">
      <Link className="namizen-privacy__back" to="/namizen">
        &larr; Back to NamiZen
      </Link>
      <h1 className="namizen-privacy__title">Privacy Policy — NamiZen</h1>
      <p className="namizen-privacy__subtitle">Last updated: June 2026</p>

      <div className="namizen-privacy__content">
        <h2>Who We Are</h2>
        <p>NamiZen is developed and operated by Cully Code LIMITED, based in United Kingdom.<br />
        If you have any questions about this privacy policy or how we handle your data, please contact us at:<br />
        Email: hello@cullycode.co.uk<br />
        Address: Belmont Suite, Paragon Business Park, Chorley New Road, Horwich, Bolton, England, BL6 6HG</p>

        <h2>The Short Version</h2>
        <p>NamiZen is a contraction timing app. All contraction data you record stays on your device. We never see it, never store it on our servers, and never share it with anyone.<br />
        We use Firebase (by Google) for anonymous crash reporting and analytics. This helps us fix bugs and understand how the app is used. It does not identify you personally.</p>

        <h2>What Data We Collect</h2>
        <h3>Data You Create — Stays On Your Device</h3>
        <p>All contraction timing data, session data, and any information you enter into NamiZen is stored locally on your device only using Apple's SwiftData framework.</p>
        <ul>
          <li>We do not collect your contraction data</li>
          <li>We do not transmit your contraction data to any server</li>
          <li>We do not have access to any timing or session information you record</li>
          <li>This data is deleted when you delete the app</li>
        </ul>

        <h3>Data Collected Automatically — Firebase Analytics</h3>
        <p>We use Google Firebase Analytics to collect anonymous usage data. This helps us understand how the app is used so we can improve it.<br />
        Firebase Analytics may collect:</p>
        <table>
          <thead>
            <tr><th>Data</th><th>Purpose</th><th>Linked to You</th></tr>
          </thead>
          <tbody>
            <tr><td>App events (e.g. app opened, session started)</td><td>Analytics</td><td>No</td></tr>
            <tr><td>Device type and iOS version</td><td>Analytics</td><td>No</td></tr>
            <tr><td>Approximate location (country/region)</td><td>Analytics</td><td>No</td></tr>
            <tr><td>Crash reports and diagnostic data</td><td>Bug fixing</td><td>No</td></tr>
            <tr><td>App performance data</td><td>Bug fixing</td><td>No</td></tr>
          </tbody>
        </table>
        <p>This data is anonymous and aggregated. It cannot be used to identify you personally. It is not linked to your name, email address, or any other personal identifier.</p>

        <h3>Data Collected Automatically — Firebase Crashlytics</h3>
        <p>We use Firebase Crashlytics to receive automated crash reports when the app encounters an error. These reports help us identify and fix bugs quickly.<br />
        Crashlytics may collect:</p>
        <ul>
          <li>The state of the app at the time of the crash</li>
          <li>Device type and iOS version</li>
          <li>A randomly generated installation identifier (not linked to your Apple ID or personal information)</li>
        </ul>

        <h2>What Data We Do Not Collect</h2>
        <p>To be explicit, NamiZen does not collect:</p>
        <ul>
          <li>Your name, email address, or contact information</li>
          <li>Your Apple ID or any account information</li>
          <li>Your location beyond approximate country/region</li>
          <li>Health data via Apple HealthKit</li>
          <li>Your contraction timing data or session history</li>
          <li>Any data that identifies you personally</li>
        </ul>

        <h2>How We Use Your Data</h2>
        <p>The anonymous analytics and crash data we receive via Firebase is used solely to:</p>
        <ul>
          <li>Fix bugs and crashes</li>
          <li>Understand which features are used</li>
          <li>Improve the performance and stability of the app</li>
        </ul>
        <p>We do not use your data for advertising. We do not sell your data. We do not share your data with third parties except as described in this policy.</p>

        <h2>Device Permissions</h2>
        <p>NamiZen does not require access to your contacts, photos, microphone, camera, location services or Apple Health data. If future versions request additional permissions, we will explain why they are needed before requesting access.</p>
        <p>NamiZen does not require users to create accounts. Contraction data is stored on your device only.  Deleting the app removes all locally stored NamiZen data.</p>

        <h2>Third Party Services</h2>
        <p>NamiZen uses the following third party services:</p>
        <h3>Google Firebase (Analytics &amp; Crashlytics)</h3>
        <ul>
          <li>Provider: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA</li>
          <li>Purpose: Anonymous analytics and crash reporting</li>
          <li>Privacy policy: https://policies.google.com/privacy</li>
          <li>Data processing: Google acts as a data processor on our behalf under a Data Processing Agreement compliant with UK GDPR and EU GDPR</li>
        </ul>
        <h3>Apple App Store</h3>
        <ul>
          <li>Provider: Apple Inc.</li>
          <li>Purpose: App distribution and payment processing</li>
          <li>Privacy policy: https://www.apple.com/legal/privacy/</li>
        </ul>

        <h2>Data Transfers Outside the UK</h2>
        <p>Firebase Analytics and Crashlytics are operated by Google LLC, based in the United States. By using NamiZen, anonymous diagnostic and analytics data may be transferred to and processed in the United States.<br />
        This transfer is governed by Google's Data Processing Agreement, which includes Standard Contractual Clauses approved for use under UK GDPR.</p>

        <h2>Data Retention</h2>
        <ul>
          <li>Your contraction data: Stored on your device only. Retained until you delete the app or clear app data.</li>
          <li>Firebase analytics data: Retained by Google for up to 14 months by default. You can request deletion — see Your Rights below.</li>
        </ul>

        <h2>Your Rights Under UK GDPR</h2>
        <p>As a UK or EU resident you have the following rights regarding your personal data:</p>
        <ul>
          <li>Right of access — request a copy of data we hold about you</li>
          <li>Right to erasure — request deletion of your data</li>
          <li>Right to restriction — request we limit how we use your data</li>
          <li>Right to object — object to our processing of your data</li>
          <li>Right to portability — request your data in a portable format</li>
        </ul>
        <p>Because we collect only anonymous analytics data, we are limited in our ability to identify data relating to a specific individual. For Firebase data specifically, you may opt out of analytics collection by contacting us or by using Apple's system-level privacy settings.<br />
        To exercise any of these rights, contact us at: hello@cullycode.co.uk<br />
        If you are unhappy with how we handle your data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO): ico.org.uk</p>

        <h2>Children's Privacy</h2>
        <p>NamiZen is intended for use by adults. We do not knowingly collect data from children under the age of 13. If you believe a child has provided us with personal information, please contact us and we will take steps to delete it.</p>

        <h2>Medical Disclaimer</h2>
        <p>NamiZen is a timing tool only. It is not a medical device and does not provide medical advice. The 5-1-1 guidance provided within the app is informational only.<br />
        Always follow the guidance of your midwife, doctor, or healthcare provider. Do not make medical decisions based solely on information provided by this app.</p>

        <h2>Changes to This Policy</h2>
        <p>We may update this privacy policy from time to time. When we do, we will update the "Last updated" date at the top of this page. We encourage you to review this policy periodically.<br />
        Continued use of NamiZen after changes are posted constitutes acceptance of the updated policy.</p>

        <h2>Contact Us</h2>
        <p>If you have any questions about this privacy policy or your data:<br />
        Email: hello@cullycode.co.uk<br />
        Address: Belmont Suite, Paragon Business Park, Chorley New Road, Horwich, Bolton, England, BL6 6HG<br />
        Developer: Cully Code LIMITED</p>
      </div>
    </div>
  </div>
);

export default NamiZenPrivacy;
